import React, { useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";

import { useGetChannels } from "@/api/useGetChannels";
import { Channel, Team, useGetTeams } from "@/api/useGetTeams";
import { useTeamsAndChannels } from "@/context/TeamsAndChannelsContext";

interface ChannelsListProps {
  onSelectChannel: React.Dispatch<
    React.SetStateAction<
      { drive_id: string; item_id: string; channelName: string; teamName: string } | undefined
    >
  >;
}

export const ChannelsList: React.FC<ChannelsListProps> = ({ onSelectChannel }) => {
  const { setTeamsAndChannels, msalConfig, teamsAndChannels } = useTeamsAndChannels();
  const [expandedTeam, setExpandedTeam] = useState<Team | null>(null);
  const { data: teams, isLoading: teamsLoading } = useGetTeams(msalConfig.backend_uri);
  const { data: channels, isLoading: channelLoading } = useGetChannels(
    msalConfig.backend_uri,
    expandedTeam?.id ?? "",
    { enabled: !!expandedTeam?.id }
  );
  useEffect(() => {
    if (teams) {
      setTeamsAndChannels(teams);
    }
  }, [teams, setTeamsAndChannels]);
  useEffect(() => {
    if (channels && expandedTeam) {
      const updatedTeams = teamsAndChannels?.map((team) =>
        team.id === expandedTeam.id ? { ...team, channels } : { ...team }
      );
      setTeamsAndChannels(updatedTeams || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);
  const [selectedIndex, setSelectedIndex] = useState<string>();

  const handleListItemClick = (channel: Channel) => {
    setSelectedIndex(channel.id);
    onSelectChannel({
      drive_id: channel.driveId,
      item_id: channel.folderId,
      channelName: channel.displayName,
      teamName: expandedTeam?.displayName || "",
    });
  };
  return (
    <Box
      sx={{
        border: "1px solid #D0D0CE",
        backgroundColor: "#F5F5F5",
        height: "calc(100% - 68px)",
        maxHeight: "calc(100% - 68px)",
        padding: "24px",
        overflowY: "auto",
      }}
    >
      <Typography fontWeight="bold">Teams and Channels</Typography>
      {teamsLoading && <Skeleton data-testid="teams_skeleton" width={150} height={30} />}
      {teamsAndChannels && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          height="95%"
          sx={{ overflowY: "auto", width: "100%" }}
        >
          {teamsAndChannels.map((team: Team) => (
            <Accordion
              disableGutters
              elevation={0}
              square
              sx={{ backgroundColor: "transparent", boxShadow: "none", width: "100%" }}
              key={team.id}
              onChange={() => {
                if (team.channels.length === 0) {
                  setExpandedTeam(team);
                }
              }}
            >
              <AccordionSummary
                sx={{
                  padding: 0,
                  minHeight: "40px",
                  flexDirection: "row-reverse",
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    marginRight: "auto",
                    transform: "none",
                  },
                }}
                expandIcon={
                  channelLoading && expandedTeam?.id === team.id ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ExpandMoreIcon />
                  )
                }
              >
                <Typography>{team.displayName}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <List sx={{ width: "100%" }}>
                  {team.channels.map((channel) => (
                    <ListItem
                      disablePadding
                      sx={{ ppadingTop: "5px", paddingLeft: "25px" }}
                      key={channel.id}
                    >
                      <ListItemButton
                        sx={{
                          padding: 0,
                          backgroundColor:
                            selectedIndex === channel.id ? "#FFFFFF !important" : "auto",
                        }}
                        selected={selectedIndex === channel.id}
                        onClick={() => handleListItemClick(channel)}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              fontWeight={selectedIndex === channel.id ? "bold" : "normal"}
                            >
                              {channel.displayName}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};
